import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus, SsoSilentRequest } from '@azure/msal-browser';
import { Path } from '@shared-enums';
import { catchError, filter, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { HttpLoaderComponent } from './core/components/http-loader/http-loader.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [HttpLoaderComponent, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  //#region Dependencies

  private msalBroadcastService = inject(MsalBroadcastService);
  private router = inject(Router);
  private msalService = inject(MsalService);

  //#endregion

  //#region Properties

  private destroy$ = new Subject<void>();

  //#endregion

  //#region Lifecycle Hooks

  ngOnInit(): void {
    this.initMsalAuthService();
    this.initMsalBroadcastService();
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  //#endregion

  //#region Init

  initMsalAuthService(): void {
    this.msalService
      .initialize()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const silentRequest: SsoSilentRequest = {
            scopes: ['User.Read'],
            loginHint: this.msalService.instance.getActiveAccount()?.username,
          };
          return this.msalService.ssoSilent(silentRequest).pipe(
            tap(authData => {
              this.msalService.instance.setActiveAccount(authData.account);
            }),
            catchError(authError => {
              localStorage.clear();
              this.router.navigateByUrl(Path.Login);
              return of(authError);
            }),
          );
        }),
      )
      .subscribe();
  }

  private initMsalBroadcastService(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$),
        catchError(authError => {
          this.router.navigateByUrl(Path.Login);
          return authError;
        }),
      )
      .subscribe();
  }

  //#endregion

  //#region Subscription

  private unsubscribe(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  //#endregion
}
